import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import {
  listFees,
  createFee,
  updateFee,
  listFeeRequestApprovals,
  getFeeRequestApproval,
  listMyFeeRequests,
  approveFeeRequest,
  rejectFeeRequest,
} from "../../services/feeService";
import {
  TOKENS,
  Icon,
  ICONS,
  Pill,
  EmptyState,
  Spinner,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
  Pagination,
  usePagination,
} from "../../components/common/DashboardUI";

// ── Modal shell (matches the other admin pages' modal styling) ──────────────
function Modal({ title, onClose, children, size = "md" }) {
  const width = size === "lg" ? "max-w-2xl" : "max-w-md";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`relative w-full ${width} bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <Icon path={ICONS.x} className="w-5 h-5" width={2} />
        </button>
        <h2 className="text-lg font-bold mb-4 pr-8" style={{ color: TOKENS.NAVY }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

// Pull the top-level message and field-level `errors` map out of an axios
// error (backend Validator returns { message, errors: { field: [msg,...] } }
// on a 422).
function errInfo(err, fallback) {
  const data = err?.response?.data;
  return { message: data?.message || fallback, fields: data?.errors || {} };
}

const money = (v) => (v == null || v === "" ? "—" : `LKR ${Number(v).toFixed(2)}`);
const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 16 ? s.slice(0, 16) : s;
};

// ── Fee create / edit form ───────────────────────────────────────────────────
function FeeForm({ mode, initial, onCancel, onSaved }) {
  const isEdit = mode === "edit";
  const [description, setDescription] = useState(initial?.description || "");
  const [fee, setFee] = useState(initial?.fee ?? "");
  const [ledgerCode, setLedgerCode] = useState(initial?.ledger_code || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const validate = () => {
    const errs = {};
    if (!description.trim()) errs.description = ["Please enter a description."];
    if (!String(fee).trim()) errs.fee = ["Please enter the fee amount."];
    else if (Number.isNaN(Number(fee)) || Number(fee) < 0) errs.fee = ["Enter a valid amount of 0 or more."];
    if (!ledgerCode.trim()) errs.ledger_code = ["Please enter a ledger code."];
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setError("");
    setSaving(true);
    try {
      const payload = { description: description.trim(), fee: Number(fee), ledger_code: ledgerCode.trim() };
      if (isEdit) {
        const updated = await updateFee(initial.id, payload);
        toast.success("Fee updated.");
        onSaved(updated);
      } else {
        const created = await createFee(payload);
        // A Bursar's submission never reaches this branch as a live fee — the
        // backend routes it into income_requests instead (still 201, but
        // `pending: true` and no `description`/`fee` on the response) — see
        // createFee's docstring. Reflect that back so the toast doesn't claim
        // the fee is live when it's actually awaiting director approval.
        toast.success(created?.pending ? created.message || "Fee request submitted for approval." : "Fee created.");
        onSaved(created);
      }
    } catch (err) {
      const info = errInfo(err, "Could not save the fee.");
      setError(info.message);
      setFieldErrors(info.fields);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className={labelClass}>Description</label>
        <input
          className={inputClass}
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            setFieldErrors((fe) => (fe.description ? { ...fe, description: null } : fe));
          }}
          maxLength={255}
          required
        />
        {fieldError("description") && <p className="text-xs text-red-600 mt-1">{fieldError("description")}</p>}
      </div>

      <div>
        <label className={labelClass}>Fee amount</label>
        <input
          type="number"
          className={inputClass}
          value={fee}
          onChange={(e) => {
            setFee(e.target.value);
            setFieldErrors((fe) => (fe.fee ? { ...fe, fee: null } : fe));
          }}
          min={0}
          step={0.01}
          required
        />
        {fieldError("fee") && <p className="text-xs text-red-600 mt-1">{fieldError("fee")}</p>}
      </div>

      <div>
        <label className={labelClass}>Ledger code</label>
        <input
          className={inputClass}
          value={ledgerCode}
          onChange={(e) => {
            setLedgerCode(e.target.value);
            setFieldErrors((fe) => (fe.ledger_code ? { ...fe, ledger_code: null } : fe));
          }}
          maxLength={100}
          required
        />
        {fieldError("ledger_code") && <p className="text-xs text-red-600 mt-1">{fieldError("ledger_code")}</p>}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3 pt-1">
        <button type="button" onClick={onCancel} className={`${btnSecondary} flex-1`}>
          Cancel
        </button>
        <button type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Fee"}
        </button>
      </div>
    </form>
  );
}

// ── Fees tab (existing CRUD list) ────────────────────────────────────────────
function FeesTab() {
  const { can } = useAuth();
  const canCreate = can("create_fees");
  const canEdit = can("edit_fees");

  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ledgerFilter, setLedgerFilter] = useState("");

  // modal: { type: "create" | "edit", fee? }
  const [modal, setModal] = useState(null);

  const fetchFees = useCallback(() => {
    setLoading(true);
    listFees()
      .then(setFees)
      .catch((err) => toast.error(err?.response?.data?.message || "Could not load fees."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchFees();
  }, [fetchFees]);

  // Ledger codes have no dedicated lookup endpoint — derive the filter's
  // options from whatever the already-loaded, real fee rows contain.
  const ledgerCodes = useMemo(
    () => [...new Set(fees.map((f) => f.ledger_code).filter(Boolean))].sort(),
    [fees]
  );
  const visibleFees = ledgerFilter ? fees.filter((f) => f.ledger_code === ledgerFilter) : fees;
  const { page, setPage, totalPages, pageItems: pagedFees } = usePagination(visibleFees, { resetKey: ledgerFilter });

  const onSaved = () => {
    setModal(null);
    fetchFees();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div />
        {canCreate && (
          <button onClick={() => setModal({ type: "create" })} className={btnPrimary}>
            <Icon path={ICONS.plus} className="w-4 h-4" width={2} />
            Add Fee
          </button>
        )}
      </div>

      {ledgerCodes.length > 1 && (
        <div className="flex flex-wrap gap-3 mb-4">
          <select
            className={`${inputClass} w-auto`}
            value={ledgerFilter}
            onChange={(e) => setLedgerFilter(e.target.value)}
          >
            <option value="">All ledger codes</option>
            {ledgerCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading fees…</p>
          </div>
        ) : visibleFees.length === 0 ? (
          <EmptyState icon={ICONS.fees} text="No fees found." />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-left min-w-[600px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Description</th>
                  <th className="py-2 px-3 font-medium">Ledger Code</th>
                  <th className="py-2 px-3 font-medium text-right">Fee</th>
                  <th className="py-2 pl-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedFees.map((f) => (
                  <tr key={f.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3 text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                      {f.description}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 font-mono whitespace-nowrap">{f.ledger_code}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 text-right whitespace-nowrap">
                      LKR {Number(f.fee).toFixed(2)}
                    </td>
                    <td className="py-3 pl-3">
                      <div className="flex items-center justify-end gap-3">
                        {canEdit && (
                          <button
                            onClick={() => setModal({ type: "edit", fee: f })}
                            className="inline-flex items-center gap-1 text-xs font-semibold"
                            style={{ color: TOKENS.PRIMARY }}
                          >
                            <Icon path={ICONS.edit} className="w-4 h-4" width={1.8} />
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pt-4 mt-2 border-t border-gray-100">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Modals */}
      {modal?.type === "create" && (
        <Modal title="Add Fee" onClose={() => setModal(null)}>
          <FeeForm mode="create" onCancel={() => setModal(null)} onSaved={onSaved} />
        </Modal>
      )}
      {modal?.type === "edit" && (
        <Modal title="Edit Fee" onClose={() => setModal(null)}>
          <FeeForm mode="edit" initial={modal.fee} onCancel={() => setModal(null)} onSaved={onSaved} />
        </Modal>
      )}
    </div>
  );
}

// ── Pending Fee Requests tab (Director/Admin approvals) ─────────────────────
// A Bursar's "Add Fee" (FeesTab above) doesn't create a live fee — it queues
// a row here instead (see FeeController::store / IncomeRequestController).
// Approving inserts it into the `income` table, which is what actually makes
// it show up on the Fees tab; rejecting just records a note. Both are
// terminal — the backend 422s if the request isn't still "Pending".
function PendingFeeRequestsTab({ canApprove, canReject }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full row object for the open detail modal
  const [viewingId, setViewingId] = useState(null); // row id currently being fetched for the detail modal
  const [acting, setActing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionNote, setRejectionNote] = useState("");

  const fetchRequests = useCallback(() => {
    setLoading(true);
    listFeeRequestApprovals()
      .then(setRequests)
      .catch((err) => toast.error(errInfo(err, "Could not load pending fee requests.").message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const closeDetail = () => {
    setSelected(null);
    setRejecting(false);
    setRejectionNote("");
  };

  // Re-fetches the single request rather than reusing the row already in
  // `requests` — the row shown in the list can be a few seconds stale
  // (another Director/Admin could have just acted on it).
  const handleView = async (id) => {
    if (viewingId) return;
    setViewingId(id);
    try {
      const r = await getFeeRequestApproval(id);
      setSelected(r);
    } catch (err) {
      toast.error(errInfo(err, "Could not load this fee request.").message);
    } finally {
      setViewingId(null);
    }
  };

  const handleApprove = async () => {
    if (!selected || acting) return;
    setActing(true);
    try {
      const r = await approveFeeRequest(selected.id);
      toast.success(r.message || "Fee request approved.");
      closeDetail();
      fetchRequests();
    } catch (err) {
      toast.error(errInfo(err, "Could not approve this fee request.").message);
    } finally {
      setActing(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!selected || acting || !rejectionNote.trim()) return;
    setActing(true);
    try {
      const r = await rejectFeeRequest(selected.id, rejectionNote.trim());
      toast.success(r.message || "Fee request rejected.");
      closeDetail();
      fetchRequests();
    } catch (err) {
      toast.error(errInfo(err, "Could not reject this fee request.").message);
    } finally {
      setActing(false);
    }
  };

  const { page, setPage, totalPages, pageItems: pagedRequests } = usePagination(requests);

  return (
    <div>
      <div className="bg-white rounded-2xl shadow-sm p-5">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : requests.length === 0 ? (
          <EmptyState icon={ICONS.fees} text="No fee requests are pending approval." />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-left min-w-[680px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Description</th>
                  <th className="py-2 px-3 font-medium">Ledger Code</th>
                  <th className="py-2 px-3 font-medium text-right">Fee</th>
                  <th className="py-2 px-3 font-medium">Requested By</th>
                  <th className="py-2 px-3 font-medium">Submitted</th>
                  <th className="py-2 pl-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedRequests.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3 text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                      {r.description}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 font-mono whitespace-nowrap">{r.ledger_code}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 text-right whitespace-nowrap">{money(r.fee)}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{r.requested_by_name || "—"}</td>
                    <td className="py-3 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                    <td className="py-3 pl-3 text-right">
                      <button
                        onClick={() => handleView(r.id)}
                        disabled={viewingId === r.id}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      >
                        {viewingId === r.id ? (
                          <Spinner className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pt-4 mt-2 border-t border-gray-100">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {selected && (
        <Modal title="Fee Request" onClose={closeDetail} size="lg">
          <div className="space-y-5">
            <div className="flex items-start justify-between gap-3">
              <p className="text-base font-bold" style={{ color: TOKENS.NAVY }}>
                {selected.description}
              </p>
              <Pill>{selected.status}</Pill>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {[
                ["Fee amount", money(selected.fee)],
                ["Ledger code", selected.ledger_code],
                ["Requested by", selected.requested_by_name],
                ["Submitted", fmtDateTime(selected.created_at)],
              ]
                .filter(([, v]) => v !== null && v !== undefined && v !== "")
                .map(([label, value]) => (
                  <div key={label} className="min-w-0">
                    <dt className="text-xs text-gray-400">{label}</dt>
                    <dd className="text-sm font-medium text-gray-700 break-words" style={{ color: TOKENS.NAVY }}>
                      {value}
                    </dd>
                  </div>
                ))}
            </dl>

            {selected.status === "Pending" && (canApprove || canReject) && (
              <div className="pt-4 border-t border-gray-100 space-y-3">
                {!rejecting ? (
                  <div className="flex flex-wrap gap-3">
                    {canApprove && (
                      <button type="button" onClick={handleApprove} disabled={acting} className={`${btnPrimary} !w-auto px-6`}>
                        {acting ? "Approving…" : "Approve"}
                      </button>
                    )}
                    {canReject && (
                      <button
                        type="button"
                        onClick={() => setRejecting(true)}
                        disabled={acting}
                        className="inline-flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 font-semibold rounded-lg text-sm px-6 py-2.5 transition"
                      >
                        Reject
                      </button>
                    )}
                  </div>
                ) : (
                  <form onSubmit={handleReject} className="space-y-3">
                    <div>
                      <label className={labelClass}>Rejection note</label>
                      <textarea
                        className={`${inputClass} resize-none`}
                        rows={2}
                        maxLength={1000}
                        value={rejectionNote}
                        onChange={(e) => setRejectionNote(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={acting || !rejectionNote.trim()}
                        className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg text-sm px-6 py-2.5 transition"
                      >
                        {acting ? "Rejecting…" : "Confirm Reject"}
                      </button>
                      <button type="button" onClick={() => setRejecting(false)} className={`${btnSecondary} !w-auto px-6`}>
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── My Fee Requests tab (Bursar — tracks their own submissions) ─────────────
// View-only: a Bursar can see where each of their own fee requests stands
// (Pending / Approved / Rejected, with the rejection note if any) but can't
// act on them — approve/reject is Director/Admin-only, on the Pending Fee
// Requests tab above.
function MyFeeRequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchRequests = useCallback(() => {
    setLoading(true);
    listMyFeeRequests()
      .then(setRequests)
      .catch((err) => toast.error(errInfo(err, "Could not load your fee requests.").message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const { page, setPage, totalPages, pageItems: pagedRequests } = usePagination(requests);

  return (
    <div>
      <div className="bg-white rounded-2xl shadow-sm p-5">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : requests.length === 0 ? (
          <EmptyState icon={ICONS.fees} text="You haven't submitted any fee requests yet." />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-left min-w-[620px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Description</th>
                  <th className="py-2 px-3 font-medium">Ledger Code</th>
                  <th className="py-2 px-3 font-medium text-right">Fee</th>
                  <th className="py-2 px-3 font-medium">Submitted</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 pl-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedRequests.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3 text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                      {r.description}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 font-mono whitespace-nowrap">{r.ledger_code}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 text-right whitespace-nowrap">{money(r.fee)}</td>
                    <td className="py-3 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                    <td className="py-3 px-3">
                      <Pill>{r.status}</Pill>
                    </td>
                    <td className="py-3 pl-3 text-right">
                      <button
                        onClick={() => setSelected(r)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50"
                      >
                        <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pt-4 mt-2 border-t border-gray-100">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {selected && (
        <Modal title="Fee Request" onClose={() => setSelected(null)}>
          <div className="space-y-5">
            <div className="flex items-start justify-between gap-3">
              <p className="text-base font-bold" style={{ color: TOKENS.NAVY }}>
                {selected.description}
              </p>
              <Pill>{selected.status}</Pill>
            </div>

            <dl className="grid grid-cols-1 gap-y-4">
              {[
                ["Fee amount", money(selected.fee)],
                ["Ledger code", selected.ledger_code],
                ["Submitted", fmtDateTime(selected.created_at)],
                ["Approved", selected.approved_at ? fmtDateTime(selected.approved_at) : null],
                ["Rejected", selected.rejected_at ? fmtDateTime(selected.rejected_at) : null],
                ["Rejection note", selected.rejection_note],
              ]
                .filter(([, v]) => v !== null && v !== undefined && v !== "")
                .map(([label, value]) => (
                  <div key={label} className="min-w-0">
                    <dt className="text-xs text-gray-400">{label}</dt>
                    <dd className="text-sm font-medium text-gray-700 break-words" style={{ color: TOKENS.NAVY }}>
                      {value}
                    </dd>
                  </div>
                ))}
            </dl>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function FeesManagement() {
  const { can } = useAuth();
  const canSubmitRequests = can("submit_income_requests");
  const canViewApprovals = can("view_income_request_approvals");
  const canApprove = can("approve_income_requests");
  const canReject = can("reject_income_requests");

  const tabs = [
    { key: "fees", label: "Fees" },
    canSubmitRequests && { key: "my-requests", label: "My Fee Requests" },
    canViewApprovals && { key: "requests", label: "Pending Fee Requests" },
  ].filter(Boolean);

  const [activeTab, setActiveTab] = useState("fees");

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
          Fees
        </h1>
        <p className="text-sm text-gray-500">Configured fee amounts used across registration, bookings and cards.</p>
      </div>

      {tabs.length > 1 && (
        <div className="flex gap-1 mb-5 border-b border-gray-200">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition ${
                activeTab === t.key ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              style={activeTab === t.key ? { color: TOKENS.PRIMARY } : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === "fees" && <FeesTab />}
      {activeTab === "my-requests" && canSubmitRequests && <MyFeeRequestsTab />}
      {activeTab === "requests" && canViewApprovals && (
        <PendingFeeRequestsTab canApprove={canApprove} canReject={canReject} />
      )}
    </div>
  );
}
